/**
 * AuthedImage — shared image component for the scrapbook, memory timeline,
 * and any future feed view.
 *
 * Why this exists
 * ---------------
 * Pre-Phase-4 we used `axios.get(..., { responseType: 'blob' })` and
 * `URL.createObjectURL` on every photo, which:
 *   • Fired all photo requests in parallel on dialog open (no viewport
 *     awareness) — Safari memory spikes on big galleries.
 *   • Bypassed the browser's HTTP cache entirely — closing and re-opening
 *     the dialog re-downloaded everything.
 *   • Held all blob URLs in memory until revoked.
 *
 * This component fixes all three by:
 *   1. Module-level `Map<key, Promise<string>>` so the same photo URL is
 *      fetched at most once per session, no matter how many components
 *      mount it (covers re-opens, re-renders, tabs).
 *   2. IntersectionObserver — actual fetch only fires when the placeholder
 *      enters the viewport (with a 400px rootMargin so we preload just-
 *      ahead-of-scroll, not way ahead).
 *   3. `decoding="async" loading="lazy"` so even after the bytes arrive,
 *      the decode work is off the main thread.
 *
 * The backend ALSO sends `Cache-Control: max-age=31536000, immutable`,
 * so re-fetches inside the same session never even hit the network — but
 * we cache promises here too because axios with responseType:'blob'
 * doesn't reuse browser cache for the resulting Object URL.
 *
 * Usage
 * -----
 *   <AuthedImage photoId={photo.id} alt={trip.title} className="..." />
 *
 * For full-resolution (lightbox / slideshow) pass variant="download".
 * Default is "feed" (1200px medium-quality).
 */
import { useEffect, useRef, useState, memo } from "react";
import axios from "axios";
import { API } from "@/App";

// Module-level cache so the same photo isn't fetched twice per session,
// even across remounts. Keyed by `${photoId}:${variant}` so feed and
// download don't collide.
const photoCache = new Map();

function loadPhoto(photoId, variant) {
  const key = `${photoId}:${variant}`;
  if (photoCache.has(key)) return photoCache.get(key);
  const path = variant === "download" ? "download" : "feed";
  const p = axios
    .get(`${API}/photos/${photoId}/${path}`, { responseType: "blob" })
    .then((res) => URL.createObjectURL(res.data))
    .catch((e) => {
      // Drop the failed promise so a future scroll can retry; otherwise
      // a transient network blip would lock that photo as broken.
      photoCache.delete(key);
      throw e;
    });
  photoCache.set(key, p);
  return p;
}

/** Public helper — useful when you need the URL outside an <img>
    (e.g. the YIR slideshow). */
export function getPhotoBlobUrl(photoId, variant = "feed") {
  return loadPhoto(photoId, variant);
}

/** Imperative cache invalidation, e.g. after editing a photo. */
export function invalidatePhotoCache(photoId) {
  for (const key of [...photoCache.keys()]) {
    if (key.startsWith(`${photoId}:`)) photoCache.delete(key);
  }
}

const AuthedImage = memo(function AuthedImage({
  photoId,
  alt = "",
  className = "",
  // `style` and `imgStyle` are deliberately separate.
  // - style:    applied to the wrapper <div> (sizing, positioning of the slot)
  // - imgStyle: applied to the inner <img> (e.g. transform/translate from the
  //             ImagePositionEditor — must NEVER be applied to the wrapper or
  //             the wrapper translates off-screen and the image looks "broken").
  style,
  imgStyle,
  variant = "feed",
  // How far ahead of the viewport to start fetching. 400px ≈ ~1 row of
  // grid cards on mobile, which feels invisibly preloaded without
  // dumping memory.
  rootMargin = "400px",
  // Optional: caller can prioritise above-the-fold images with eager.
  eager = false,
  onClick,
  testId,
}) {
  const [src, setSrc] = useState(null);
  const [errored, setErrored] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!photoId) return undefined;
    let cancelled = false;

    // No `startedRef` guard — `loadPhoto` is already deduped via the
    // module-level photoCache, so calling it twice (e.g. React 18/19
    // StrictMode dev double-mount) returns the SAME promise. Calling
    // setSrc twice with the same URL is harmless. Without this, the
    // first mount's cleanup sets cancelled=true, and the second mount
    // skips start() entirely (because startedRef is true), so neither
    // closure ever calls setSrc — the image stays in skeleton forever.
    const start = () => {
      loadPhoto(photoId, variant)
        .then((url) => { if (!cancelled) setSrc(url); })
        .catch(() => { if (!cancelled) setErrored(true); });
    };

    if (eager) {
      start();
      return () => { cancelled = true; };
    }

    // IntersectionObserver — only fetch when near the viewport.
    if (!("IntersectionObserver" in window)) {
      // Old browser fallback — just fetch immediately.
      start();
      return () => { cancelled = true; };
    }

    const node = ref.current;
    if (!node) return () => { cancelled = true; };
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            start();
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin },
    );
    io.observe(node);
    return () => {
      cancelled = true;
      io.disconnect();
    };
  }, [photoId, variant, eager, rootMargin]);

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden ${className}`}
      style={style}
      onClick={onClick}
      data-testid={testId}
    >
      {/* Skeleton placeholder — softly pulses while we wait. Stays visible
          behind the image so layout never shifts when bytes arrive. */}
      {!src && !errored && (
        <div className="absolute inset-0 bg-gradient-to-br from-muted/50 via-muted/30 to-muted/50 animate-pulse" />
      )}
      {errored && (
        <div className="absolute inset-0 flex items-center justify-center text-[10px] text-muted-foreground/60 bg-muted">
          Photo unavailable
        </div>
      )}
      {src && (
        <img
          src={src}
          alt={alt}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={eager ? "high" : "low"}
          // No opacity-0 + onLoad fade-in pattern — when a blob URL is
          // already cached (very common after the first scroll) the
          // browser fires `load` synchronously BEFORE React attaches the
          // listener, so the image stays invisible at opacity:0 forever.
          // The skeleton placeholder is enough visual feedback while we
          // wait, and the image just appears the moment src is set.
          className="w-full h-full object-cover"
          style={imgStyle || undefined}
        />
      )}
    </div>
  );
});

export default AuthedImage;
